//
// Decide which scout photos need to be cached locally depending on user, role, and assigned scouts
//
import { Injectable } from '@angular/core';
import { AppState } from '../app-state';
import { ScoutPointLocalPhoto, ScoutPointLocalPhotoUrl, ScoutPointPhoto } from '../models/scout-task';
import { ScoutLocalDbService, TestbedDexieCollectionService } from './scout-local-db.service';
import { ScoutPointLocalPhotoUrlService } from './scout-point-local-photo-url.service';


@Injectable({ providedIn: 'root' })
export class ScoutPointLocalPhotoService extends TestbedDexieCollectionService<ScoutPointLocalPhoto> {


  constructor( 
      appState : AppState, 
      protected scoutLocalDb: ScoutLocalDbService,
      protected scoutPointLocalPhotoUrlService: ScoutPointLocalPhotoUrlService ) {
    super(appState)
    this.doWhenReady( () => this.removeOldLocalPhotos() )
  }

  get dexieStore(){
    return this.scoutLocalDb.scoutPointLocalPhotos
  }

  async flagForUpload( localPhoto: ScoutPointLocalPhoto ) {
    try {
      await this.update( localPhoto, { errorMsg: '', uploadDate: '', uploadStartDate: '' })
      return localPhoto
    } catch ( error ) {
      console.error('Failed updating local photo in indexeddb for retry of upload')
      console.error(error)
      throw error
    }
  }

  async isLocalPhotoFileCurrent( photo : ScoutPointPhoto ) : Promise<boolean> {
    try {
      const fileDoc = await this.getById( photo._id )
      const bIsCurrent = fileDoc && fileDoc.name == photo.fileName && fileDoc.checksum == photo.fileChecksum
      return bIsCurrent
    } catch ( error ) {
      console.error( this.extractErrorMsg(error) )
      throw error
    }
  }
  
  
  async loadLocalPhotoFile( id: string, checksum: string ) {
    try {
      const localPhotoDoc = await this.getById(id)
      if ( localPhotoDoc ) {
        if ( checksum != localPhotoDoc.checksum ) {
          console.warn('Local photo file checksum does not match checksum from photo document. id=' + id)
          return null
        }
        const dataUrl = (await this.scoutPointLocalPhotoUrlService.getById(id))?.url
        console.log('Loaded ' + localPhotoDoc.name + ' from local storage. (size:' + dataUrl?.length + ' bytes)' )
        return dataUrl
      }
    } catch ( error ) {
      console.error('Failed retrieving file data associated with photo. id=' + id)
      console.error( this.extractErrorMsg(error) )
      throw error
    }
  }


  async removeLocalPhotoFile( id: string ) {
    let errors = []
    try {
      const doc = await this.getById(id)
      if ( doc  ) {
        await this.dexieStore.delete( doc._id )
      }
    } catch ( error ) {
      console.error('Failed removing photo cache metadata associated with photo. id=' + id)
      console.error( this.extractErrorMsg(error) )
      errors.push(error)
    }
    try {
      const doc = await this.scoutPointLocalPhotoUrlService.getById(id)
      if ( doc ) {
        await this.scoutPointLocalPhotoUrlService.dexieStore.delete( doc._id )
      }
    } catch ( error ) {
      console.error('Failed removing photo cache url data associated with photo. id=' + id)
      console.error( this.extractErrorMsg(error) )
      errors.push(error)
    }
    if ( errors.length ) {
      throw errors[0]
    }
  }


  async saveLocalPhotoFile( photo : ScoutPointPhoto, photoUrl : string ) {
    try {
      const localPhoto : ScoutPointLocalPhoto = {
        _id: photo._id,
        checksum: photo.fileChecksum,
        name: photo.fileName,
        uploadDate: photo.uploadDate || '',
        date: this.nowTimeStamp(),
        errorMsg: '',
        retryCnt: 0,
        uploadStartDate: ''
      }      
      await this.dexieStore.put( localPhoto )
      const localPhotoUrl : ScoutPointLocalPhotoUrl = {
        _id: photo._id,
        url: photoUrl
      }      
      await this.scoutPointLocalPhotoUrlService.dexieStore.put( localPhotoUrl )
      console.log('Saved ' + photo.fileName + ' to local storage (size=' + photoUrl?.length + '). id=' + localPhoto._id )    
      return localPhoto
    } catch ( error ) {
      console.error('Failed savinging file data associated with photo. name=' + photo.fileName + ', id=' + photo._id)
      console.error( this.extractErrorMsg(error) )
      throw error
    }
  }
  
  
  // localPhoto.date is not indexed so only call this on startup
  async removeOldLocalPhotos( maxPhotoCacheAgeDays : number = 45 ) {
    const maxAgeSecs = maxPhotoCacheAgeDays * 60 * 60 * 24
    const minKeepDate = new Date(Date.now() - maxAgeSecs);
    const minKeepTimestamp = this.timestamp(minKeepDate)
    const oldPhotos = await this.dexieStore.where('uploadDate').notEqual('')
        .filter( doc => (doc.date||'').localeCompare(minKeepTimestamp) < 0 ).toArray()
    try {
      for( const oldPhoto of oldPhotos ) {
        console.log(`Photo "${oldPhoto.name}" cache age more than ${maxPhotoCacheAgeDays} days.  Removing local copy (cached: ${oldPhoto.date}, uploaded: ${oldPhoto.uploadDate})`)
        await this.removeLocalPhotoFile(oldPhoto._id)
      }
      //await this.dexieStore.bulkDelete( oldPhotos.map(p=>p._id) )
      //await this.scoutPointLocalPhotoUrlService.dexieStore.bulkDelete( oldPhotos.map(p=>p._id) )
    } catch ( error : any ) {
      console.error(error)
      this.publishError(error,this)
    }
  }



}